import { useEffect } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

/**
 * A single chat bubble. Tailwind + theme tokens (no chat-ui-kit). Keeps the
 * lightweight markdown → HTML rendering and the clickable case-link behavior.
 */
function FormattedMessage({ message, chatContext }) {
  const history = useHistory();

  useEffect(() => {
    const handler = e => {
      if (e.target.classList.contains('case-link')) {
        e.preventDefault();
        const { id, slug } = e.target.dataset;
        history.push(`/workflow/${slug}/${id}`);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [history, message]);

  const isUser = message.direction === 'outgoing';
  const isSystem = message.sender === 'System';
  const isError = !!message.error;
  const streaming = !!message.streaming;
  const hasText = !!(message.message && message.message.length);

  const bubble = isError
    ? 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/25 rounded-2xl rounded-bl-md'
    : isUser
      ? 'theme-accent-bg text-white rounded-2xl rounded-br-md'
      : isSystem
        ? 'theme-bg-tertiary theme-text-secondary rounded-2xl'
        : 'theme-bg-secondary theme-text-primary border theme-border rounded-2xl rounded-bl-md';

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} px-3 py-1`}>
      <div className={`ai-chat-bubble max-w-[82%] px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm ${bubble}`}>
        {hasText ? (
          <div
            className="markdown-content break-words"
            style={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: processMarkdownAndHtml(message, chatContext?.identifierCases) }}
          />
        ) : null}
        {streaming ? (
          <span
            className={`ml-0.5 inline-block h-3.5 w-[2px] translate-y-[2px] animate-pulse rounded-full ${
              isUser ? 'bg-white/80' : 'theme-accent-bg'
            }`}
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

const processMarkdownAndHtml = (model, identifierCases = []) => {
  const processedContent = (model.message || '').replace(/\n/g, '<br />');

  return processedContent
    .replace(/###\s?(.*?)(<br \/>|$ )/g, '<strong>$1</strong><br>')
    .replace(/##\s?(.*?)(\n|$)/g, '<h2>$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/(\d{2}FF-\d{6}-[A-Z]{2})/g, match => {
      const uweEntity = identifierCases.find(c => c.number === match);
      return uweEntity
        ? `<a href="#" data-id="${uweEntity.id}" data-slug="${uweEntity.slug}" class="case-link underline underline-offset-2 font-medium">${match}</a>`
        : match;
    });
};

export default FormattedMessage;
